import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useSearchEventLogs } from '../../hooks/api/use-event-logs.hook';
import { EventType } from '../../../../types/constants/event-type';
import { useCreateArcgisSchema } from '../../hooks/api/use-arcgis-schemas.hook';
import { LoadingIndicator } from '../LoadingIndicator';

dayjs.extend(utc);
dayjs.extend(timezone);

export const ExportArcgisSchema = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [createArcgisSchema, { loading: creatingArcgisSchema, error: createError }] = useCreateArcgisSchema();
  const { loading: loadingEventLogs, response: eventLogsResponse } = useSearchEventLogs({
    limit: 1,
    types: [EventType.ARCGIS_SCHEMA_CREATED],
  });

  const lastGenerated = eventLogsResponse?.pages?.flatMap(({ result }) => result)?.[0]?.createdAt ?? null;

  const lastGeneratedFormatted = lastGenerated
    ? dayjs(lastGenerated).tz('America/Los_Angeles').format('MMMM D, YYYY h:mm A [GMT]Z')
    : 'No schemas have been generated.';

  const handleGenerate = async () => {
    await createArcgisSchema({
      datasets: [
        'Control',
        'Hydrographic',
        'Roadbed',
        'Roadside',
        'Structures',
        'Traffic_Control',
        'Utilities',
        'Vegetation',
        'ROW',
      ],
    });
    queryClient.invalidateQueries({
      queryKey: ['eventLog', 'search', { limit: 1, types: [EventType.ARCGIS_SCHEMA_CREATED] }],
    });
  };

  const renderContent = () => {
    if (loadingEventLogs) {
      return <LoadingIndicator />;
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Box>
            <Typography variant="body2">
              <Typography component="strong" sx={{ fontWeight: 700 }}>
                Last generated:
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: 'black' }}>
              {lastGeneratedFormatted}
            </Typography>
          </Box>
          <LoadingButton
            sx={{ maxHeight: 40 }}
            variant="contained"
            onClick={handleGenerate}
            loading={creatingArcgisSchema}
          >
            Generate
          </LoadingButton>
        </Box>
        {createError && (
          <Alert sx={{ mt: '1rem' }} severity="error">
            {createError?.response?.data?.message}
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={true}>
      <DialogTitle>Create GIS Schema</DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => navigate('..')} color="primary">
          Cancel
        </Button>
        <Button
          disabled={creatingArcgisSchema}
          color="primary"
          type="submit"
          variant="contained"
          onClick={() => {
            window.location.href = import.meta.env.VITE_GIS_DOWNLOAD_URL;
          }}
        >
          DOWNLOAD
        </Button>
      </DialogActions>
    </Dialog>
  );
};
